import * as React from "react";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Converts a YYYY-MM-DD value to dd/mm/yyyy for display.
 */
function toDisplay(isoValue: string): string {
  if (!isoValue) return "";
  const parts = isoValue.split("-");
  if (parts.length !== 3) return "";
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

/**
 * Converts a dd/mm/yyyy string to YYYY-MM-DD, returning "" if invalid.
 */
function toISO(display: string): string {
  const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";
  const [, d, m, y] = match;
  const day = parseInt(d, 10);
  const month = parseInt(m, 10);
  const year = parseInt(y, 10);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return "";
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return "";
  return `${y}-${m}-${d}`;
}

export interface DateInputProps {
  id?: string;
  value: string; // YYYY-MM-DD
  onChange: (isoValue: string) => void;
  className?: string;
  placeholder?: string;
}

function DateInput({ id, value, onChange, className, placeholder = "dd/mm/yyyy" }: DateInputProps) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(() => toDisplay(value));

  // Keep the text input in sync when the external value changes (e.g. from calendar pick).
  const lastExternalRef = React.useRef(value);
  React.useEffect(() => {
    if (value !== lastExternalRef.current) {
      lastExternalRef.current = value;
      setText(toDisplay(value));
    }
  }, [value]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;

    // Strip non-digit, non-slash characters
    raw = raw.replace(/[^\d/]/g, "");

    // Auto-insert slashes after dd and mm
    const digits = raw.replace(/\//g, "");
    if (digits.length <= 2) {
      raw = digits;
    } else if (digits.length <= 4) {
      raw = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      raw = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }

    setText(raw);

    const iso = toISO(raw);
    if (iso) {
      lastExternalRef.current = iso;
      onChange(iso);
    } else if (raw === "") {
      lastExternalRef.current = "";
      onChange("");
    }
  };

  const handleBlur = () => {
    // On blur, if the typed text isn't a valid date, revert to the current value.
    const iso = toISO(text);
    if (!iso && text !== "") {
      setText(toDisplay(value));
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const iso = `${y}-${m}-${d}`;
      lastExternalRef.current = iso;
      setText(`${d}/${m}/${y}`);
      onChange(iso);
    }
    setOpen(false);
  };

  const selectedDate = React.useMemo(() => {
    if (!value) return undefined;
    const d = new Date(value + "T00:00:00");
    return isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  return (
    <div className="relative flex items-center">
      <input
        id={id}
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={10}
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-background px-3 py-1 text-base transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          "pr-9",
          className,
        )}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="absolute right-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Open calendar"
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate}
            captionLayout="dropdown"
            startMonth={new Date(1900, 0)}
            endMonth={new Date(2100, 11)}
            onSelect={handleCalendarSelect}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { DateInput };
